import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, type AccountType } from "@/hooks/useAuth";
import SiteHeader from "@/components/feature/SiteHeader";
import Footer from "@/pages/home/components/Footer";

export default function SignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<"type" | "form">("type");
  const [accountType, setAccountType] = useState<AccountType>("worker");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [showPass, setShowPass] = useState(false);

  const handleTypeSelect = (type: AccountType) => {
    setAccountType(type);
    setStep("form");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) return setError("Please enter your name.");
    if (!email.includes("@")) return setError("Please enter a valid email address.");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    if (password !== confirmPassword) return setError("Passwords do not match.");
    const result = signup(name.trim(), email.trim(), password, accountType);
    if (!result) return setError("An account with this email already exists. Try signing in instead.");
    navigate(result.type === "employer" ? "/dashboard/employer" : "/dashboard");
  };

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <SiteHeader />
      <main className="pt-28 pb-16 min-h-screen">
        <div className="max-w-5xl mx-auto px-4 md:px-8">

          {/* Header */}
          <div className="text-center mb-12">
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-3.5 py-1.5 rounded-full mb-4">
              Create Account
            </span>
            <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-2">
              Join Gig <span className="italic">n&apos;</span> Cash Today
            </h1>
            <p className="text-gray-500 text-sm">
              Already have an account?{" "}
              <Link to="/login" className="text-emerald-600 font-semibold hover:underline">
                Sign in
              </Link>
            </p>
          </div>

          {step === "type" ? (
            /* ── Account Type Selection ── */
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              {/* Worker Card */}
              <button
                onClick={() => handleTypeSelect("worker")}
                className="group text-left bg-white rounded-2xl border-2 border-gray-200 hover:border-emerald-500 transition-all duration-200 overflow-hidden cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://public.readdy.ai/ai/img_res/80637843fd4257434993cdeb6ded1c24.jpg"
                    alt="Worker"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                      For Workers
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-50 group-hover:bg-emerald-100 transition-colors">
                      <i className="ri-user-3-line text-emerald-600 text-lg" />
                    </div>
                    <div>
                      <h2 className="font-extrabold text-gray-900 text-lg">I'm a Worker</h2>
                      <p className="text-xs text-gray-500">Looking for jobs &amp; income</p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {["Find local gigs and full-time work", "Build a capability-based profile", "Track earnings and apply instantly", "Get AI-matched job recommendations"].map(item => (
                      <li key={item} className="flex items-center gap-2 text-xs text-gray-600">
                        <i className="ri-check-line text-emerald-500 flex-shrink-0" />{item}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 flex items-center gap-1.5 text-sm font-bold text-emerald-600 group-hover:gap-3 transition-all">
                    Sign up as Worker <i className="ri-arrow-right-line" />
                  </div>
                </div>
              </button>

              {/* Employer Card */}
              <button
                onClick={() => handleTypeSelect("employer")}
                className="group text-left bg-white rounded-2xl border-2 border-gray-200 hover:border-emerald-500 transition-all duration-200 overflow-hidden cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20business%20owner%20employer%20at%20modern%20office%20reviewing%20applications%20laptop%20desk%20bright%20clean%20workspace%20confident%20natural%20light%20photography&width=500&height=300&seq=se1&orientation=landscape"
                    alt="Employer"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <span className="bg-gray-900 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                      For Employers
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 group-hover:bg-gray-200 transition-colors">
                      <i className="ri-building-2-line text-gray-700 text-lg" />
                    </div>
                    <div>
                      <h2 className="font-extrabold text-gray-900 text-lg">I'm an Employer</h2>
                      <p className="text-xs text-gray-500">Looking to hire fast</p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {["Post jobs in under 2 minutes", "Browse capability-matched workers", "Hire same-day for urgent needs", "Manage applicants in one dashboard"].map(item => (
                      <li key={item} className="flex items-center gap-2 text-xs text-gray-600">
                        <i className="ri-check-line text-emerald-500 flex-shrink-0" />{item}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 flex items-center gap-1.5 text-sm font-bold text-gray-900 group-hover:gap-3 transition-all">
                    Sign up as Employer <i className="ri-arrow-right-line" />
                  </div>
                </div>
              </button>
            </div>
          ) : (
            /* ── Signup Form ── */
            <div className="max-w-md mx-auto">
              {/* Back + Selected Type */}
              <div className="flex items-center gap-3 mb-6">
                <button
                  onClick={() => setStep("type")}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <i className="ri-arrow-left-s-line text-gray-600" />
                </button>
                <div className={`flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold ${
                  accountType === "worker"
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-gray-100 text-gray-700"
                }`}>
                  <i className={accountType === "worker" ? "ri-user-3-line" : "ri-building-2-line"} />
                  {accountType === "worker" ? "Worker Account" : "Employer Account"}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 p-8">
                <h2 className="text-xl font-extrabold text-gray-900 mb-1">
                  {accountType === "worker" ? "Create your Worker profile" : "Set up your Employer account"}
                </h2>
                <p className="text-xs text-gray-500 mb-6">
                  {accountType === "worker"
                    ? "Start earning in minutes — no experience required."
                    : "Post your first job today and hire same-day."}
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      {accountType === "worker" ? "Full Name" : "Your Name"}
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder={accountType === "worker" ? "e.g. Marcus Rivera" : "e.g. Sarah Chen"}
                      className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-emerald-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Email Address</label>
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-emerald-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Password</label>
                    <div className="relative">
                      <input
                        type={showPass ? "text" : "password"}
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder="At least 6 characters"
                        className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 pr-10 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-emerald-400 transition-colors"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPass(!showPass)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                      >
                        <i className={showPass ? "ri-eye-off-line" : "ri-eye-line"} />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Confirm Password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      placeholder="Repeat password"
                      className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-emerald-400 transition-colors"
                    />
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2.5">
                      <i className="ri-error-warning-line" />{error}
                    </div>
                  )}

                  <button
                    type="submit"
                    className={`w-full py-3 rounded-lg text-sm font-bold text-white transition-colors whitespace-nowrap ${
                      accountType === "worker"
                        ? "bg-emerald-500 hover:bg-emerald-600"
                        : "bg-gray-900 hover:bg-gray-800"
                    }`}
                  >
                    Create {accountType === "worker" ? "Worker" : "Employer"} Account
                    <i className="ri-arrow-right-line ml-2" />
                  </button>
                </form>

                <p className="text-xs text-gray-400 text-center mt-5">
                  By signing up you agree to our{" "}
                  <a href="#" className="underline">Terms</a> &amp;{" "}
                  <a href="#" className="underline">Privacy Policy</a>
                </p>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
